import React, { FunctionComponent, useState } from 'react'
import styles from './ProjectCloseDialog.module.sass'
import { Dialog } from '../../../etc/Dialog/Dialog'
import { useRootSelector } from '../../../../utils/hooks'
import {
    flatMapSelector,
    flowSelectors,
    objectsSelector,
    objectTasksSelector,
    pipeSelectors,
    projectObjectsSelector,
    tasksSelector
} from '../../../../utils/selectors'
import { Project, ProjectCloseData, ProjectStatus } from '../../../../responses/projects'
import { ProjectWorkObject } from '../../../../responses/objects'
import { ID } from '../../../../types/UtilityTypes'
import { Task } from '../../../../responses/tasks'
import OkCancel from '../../../controls/OkCancel/OkCancel'
import ObjectDesignPicker, { isObjectIncomplete } from '../../../etc/ObjectDesignPicker/ObjectDesignPicker'
import { groupBy } from 'fp-ts/es6/NonEmptyArray'
import { flow } from 'fp-ts/es6/function'
import { filter } from 'fp-ts/es6/Array'
import { TenderStatus } from '../../pickers/TenderPicker/TenderPicker'
import { entityReplace, getId } from '../../../../utils/entity-utils'
import { insertAt } from 'fp-ts/es6/Record'
import { unboxRecord } from '../../../../utils/utils'
import { RequestStatus } from '../../../../responses/response'
import { constructCloseData } from '../ObjectCloseDialog/ObjectCloseDialog'
import QuillEditor from '../../../etc/Editor/QuillEditor'

const group = flow(
    filter((task: Task) => task.tender_id !== TenderStatus.CANCELED),
    groupBy((task: Task) => task.project_work_object_id.toString()),
)

interface Props {
    project: Project,
    onChange: (data: ProjectCloseData) => void,
    onCancel: () => void,
}

const customTasksSelector = flowSelectors(flatMapSelector(objectTasksSelector), tasksSelector)

const ProjectCloseDialog: FunctionComponent<Props> = props => {
    const storeObjects = useRootSelector(pipeSelectors(props.project.id, projectObjectsSelector, objectsSelector))
    const fetching = useRootSelector(state => state.data.project.closeStatus === RequestStatus.FETCHING)
    const storeTasks = useRootSelector(customTasksSelector(storeObjects.map(getId)))

    const [comment, setComment] = useState('')
    const [objects, setObjects] = useState<ProjectWorkObject[]>(storeObjects)
    const [tasks, setTasks] = useState<Record<ID, Task[]>>(group(storeTasks))

    const open = props.project.status_id !== ProjectStatus.CLOSED
    const text = open ? 'Закрыть' : 'Открыть'
    const incomplete =  open && objects.some(object => isObjectIncomplete(object, tasks[object.id] ?? []))

    return <Dialog
        className={styles.dialog}
        zIndex={200}
        headerClassName={styles.header}
        onClose={props.onCancel}
        header={<div>
            {
                incomplete && <div className={styles.warning}>Проставьте статусы и исполнителей</div>
            }
            <div>{text} проект</div>
        </div>}
        actions={<OkCancel
            disabled={incomplete || fetching}
            text={text}
            onCancel={props.onCancel}
            onOk={() => props.onChange({
                id: props.project.id,
                project_work_objects: objects.map(object => constructCloseData(object, tasks[object.id])),
                comment: unboxRecord({ text: comment }) // todo прицепить проверку пустоты
            })}
        />}
    >
        {
            open && objects.map(object => <ObjectDesignPicker
                key={object.id}
                object={object}
                tasks={tasks[object.id] ?? []}
                tasksChange={tasks => setTasks(insertAt(object.id.toString(), tasks))}
                objectChange={object => setObjects(entityReplace(object))}
            />)
        }
        <QuillEditor className={styles.editor} value={comment} onChange={setComment}/>
    </Dialog>
}

export default ProjectCloseDialog